import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type Asset, type AssetKind } from "../api.js";
import { Button, COLORS, Empty, Input } from "../ui.js";
import { KIND_ICON, KIND_LABEL, humanSize } from "../lib/assetUpload.js";

interface Props {
  kind: AssetKind;
  selectedId: string;
  onSelect: (asset: Asset) => void;
}

/**
 * Sélecteur d'asset utilisé dans les blocs (VIDEO, AUDIO, PDF, IMAGE).
 * Bouton qui ouvre une modale listant la bibliothèque filtrée par type.
 */
export function AssetSelector({ kind, selectedId, onSelect }: Props) {
  const [open, setOpen] = useState(false);

  const selected = useQuery({
    queryKey: ["asset", selectedId],
    queryFn: () => api.get<Asset>(`/assets/${selectedId}`),
    enabled: !!selectedId,
  });

  return (
    <div>
      {selected.data ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: 10,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            background: "#fafaf8",
          }}
        >
          <Thumb asset={selected.data} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.data.name}
            </div>
            <div style={{ fontSize: 11, color: COLORS.muted }}>
              {KIND_LABEL[selected.data.kind]} · {humanSize(selected.data.sizeBytes)}
            </div>
          </div>
          <Button variant="ghost" onClick={() => setOpen(true)} style={{ padding: "6px 10px", fontSize: 12 }}>
            Changer
          </Button>
        </div>
      ) : (
        <Button onClick={() => setOpen(true)} variant="ghost" style={{ width: "100%" }}>
          {KIND_ICON[kind]} Choisir {KIND_LABEL[kind].toLowerCase()}
        </Button>
      )}

      {open && (
        <AssetPickerModal
          kind={kind}
          currentId={selectedId}
          onClose={() => setOpen(false)}
          onPick={(a) => {
            onSelect(a);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AssetPickerModal({
  kind,
  currentId,
  onClose,
  onPick,
}: {
  kind: AssetKind;
  currentId: string;
  onClose: () => void;
  onPick: (a: Asset) => void;
}) {
  const [search, setSearch] = useState("");
  const list = useQuery({
    queryKey: ["assets-picker", kind, search],
    queryFn: () =>
      api.get<{ items: Asset[] }>(
        `/assets?${new URLSearchParams({
          kind,
          ready: "true",
          ...(search ? { search } : {}),
          take: "60",
        })}`
      ),
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20,15,30,0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "min(900px, 100%)",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${COLORS.border}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
            Choisir {KIND_LABEL[kind].toLowerCase()}
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            style={{ width: 240 }}
          />
          <Button variant="ghost" onClick={onClose} style={{ padding: "6px 14px" }}>✕</Button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {list.isLoading && <Empty>Chargement…</Empty>}
          {list.data?.items.length === 0 && (
            <Empty>
              Aucun {KIND_LABEL[kind].toLowerCase()} dans la bibliothèque. Uploadez d'abord via la page Bibliothèque.
            </Empty>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 12,
            }}
          >
            {list.data?.items.map((a) => {
              const isCurrent = a.id === currentId;
              return (
                <button
                  key={a.id}
                  onClick={() => onPick(a)}
                  style={{
                    background: "#fff",
                    border: `2px solid ${isCurrent ? COLORS.accent : COLORS.border}`,
                    borderRadius: 8,
                    overflow: "hidden",
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    fontFamily: "inherit",
                    color: COLORS.text,
                    textAlign: "left",
                  }}
                >
                  <Thumb asset={a} size="large" />
                  <div style={{ padding: 10, flex: 1, display: "flex", flexDirection: "column" }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={a.name}
                    >
                      {a.name}
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 2 }}>
                      {humanSize(a.sizeBytes)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Thumb({ asset, size = "small" }: { asset: Asset; size?: "small" | "large" }) {
  const dim = size === "large" ? { height: 100, fontSize: 28 } : { width: 44, height: 44, fontSize: 18 };
  const isImage = asset.kind === "IMAGE";
  const bg = isImage && asset.cdnUrl
    ? `#000 url(${asset.cdnUrl}) center/cover`
    : asset.kind === "VIDEO" && asset.thumbnailUrl
      ? `#000 url(${asset.thumbnailUrl}) center/cover`
      : "#1a1a1a";
  return (
    <div
      style={{
        ...dim,
        background: bg,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        borderRadius: size === "small" ? 6 : 0,
      }}
    >
      {!isImage && (asset.kind !== "VIDEO" || !asset.thumbnailUrl) && KIND_ICON[asset.kind]}
    </div>
  );
}
